import React from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { ArrowLeft, ArrowRight } from "lucide-react";
import Seo from "@/components/Seo";
import { Breadcrumb } from "@/components/PageHero";
import { MaskedLines, Reveal } from "@/components/Reveal";
import CTASection from "@/components/CTASection";
import { getArticle, ARTICLES } from "@/data/site";

export default function BlogArticle() {
  const { slug } = useParams();
  const article = getArticle(slug);

  if (!article) return <Navigate to="/blog" replace />;

  const related = ARTICLES.filter((a) => a.slug !== slug).slice(0, 3);
  const schema = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: article.title,
    datePublished: article.date,
    author: { "@type": "Organization", name: "FuiteX" },
    image: article.image,
  };

  return (
    <>
      <Seo
        title={`${article.title} | FuiteX`}
        description={article.excerpt}
        canonical={`/blog/${article.slug}`}
        schema={schema}
      />

      <section className="relative bg-ink pt-[76px] overflow-hidden grain">
        <div className="absolute -top-20 right-0 h-80 w-80 rounded-full bg-aqua/20 blur-3xl" />
        <div className="container-x py-16 sm:py-20 relative z-10 max-w-4xl">
          <Breadcrumb items={[{ label: "Blog", to: "/blog" }, { label: article.category }]} />
          <div className="flex items-center gap-3 text-xs mb-6">
            <span className="rounded-full bg-aqua/15 px-3 py-1 font-bold text-aqua">{article.category}</span>
            <span className="text-white/50">{new Date(article.date).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" })}</span>
          </div>
          <h1 className="text-3xl sm:text-5xl font-extrabold tracking-tighter text-white leading-[1.08]">
            <MaskedLines lines={[article.title]} />
          </h1>
        </div>
      </section>

      <article className="bg-white py-16">
        <div className="container-x max-w-3xl">
          <Reveal className="rounded-3xl overflow-hidden border border-ink/10 mb-12">
            <img src={article.image} alt={article.title} className="w-full h-auto object-cover" />
          </Reveal>
          <div className="space-y-6">
            {article.content.map((p, i) => (
              <Reveal key={i} delay={i * 0.03}>
                <p className="text-lg text-ink/75 leading-relaxed">{p}</p>
              </Reveal>
            ))}
          </div>

          <div className="mt-12 rounded-3xl bg-mist p-8">
            <p className="overline text-aqua mb-3">Besoin d'un diagnostic&nbsp;?</p>
            <p className="text-ink/75 mb-5">Nos experts localisent votre fuite sans casse. Devis gratuit en Île-de-France.</p>
            <Link to="/contact" className="btn-cta">Demander un devis <ArrowRight size={16} /></Link>
          </div>

          <Link to="/blog" className="mt-10 inline-flex items-center gap-2 text-sm font-bold text-ink/60 hover:text-aqua transition-colors">
            <ArrowLeft size={16} /> Retour au blog
          </Link>
        </div>
      </article>

      <section className="bg-mist py-20">
        <div className="container-x">
          <h2 className="text-2xl font-bold tracking-tight text-ink mb-8">À lire aussi</h2>
          <div className="grid gap-6 md:grid-cols-3">
            {related.map((a) => (
              <Link key={a.slug} to={`/blog/${a.slug}`} className="group block rounded-3xl border border-ink/10 bg-white overflow-hidden hover:border-aqua/40 transition-colors">
                <img src={a.image} alt={a.title} className="h-40 w-full object-cover" />
                <div className="p-5">
                  <h3 className="font-bold text-ink group-hover:text-aqua transition-colors leading-snug">{a.title}</h3>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <CTASection />
    </>
  );
}
